/**
 * Artist API.
 *
 * Two things about the engine that the UI has to be honest about, both verified
 * against the running service rather than assumed:
 *
 *   1. There is NO 'declined' agreement status. An artist declining a request
 *      sets it to 'cancelled', the same value an organizer cancellation
 *      produces. The label says "Declined / cancelled" because that is the only
 *      state the engine has.
 *
 *   2. Accepting is not a state change. PUT /bookings/:id/respond with
 *      action='accept' leaves the agreement 'pending' and records an audit
 *      entry; signatures are what move it to 'signed'. So the accept path here
 *      goes straight through to signing rather than pretending acceptance
 *      settled anything.
 */

import { api } from './apiClient';
import { AGREEMENT_LABEL, type AgreementRow, type AgreementStatus } from './organizer';

// The status set and labels are shared with the organizer side (task 31) rather
// than redefined, so the same agreement never reads differently to the two
// parties looking at it.
export { AGREEMENT_LABEL };
export type { AgreementRow, AgreementStatus };

/** GET /api/bookings/mine — works for artists; raw snake_case, same as the organizer view. */
export const listMyBookings = () => api.get<AgreementRow[]>('/bookings/mine');

// ── Profile ─────────────────────────────────────────────────────────────────

export interface ArtistProfile {
  artistProfileId: string;
  stageName: string;
  bio: string | null;
  genre: string | null;
  primaryMarket: string | null;
  profileImageUrl: string | null;
  coverImageUrl: string | null;
  tier: string;
  /** Read-only. Booking requires an admin to approve the profile. */
  approved: boolean;
  approvedAt: string | null;
  social: {
    instagramHandle: string | null;
    instagramFollowers: number | null;
    tiktokHandle: string | null;
    tiktokFollowers: number | null;
    spotifyMonthlyListeners: number | null;
    youtubeSubscribers: number | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ProfileInput {
  stageName?: string;
  bio?: string | null;
  genre?: string | null;
  primaryMarket?: string | null;
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
  instagramHandle?: string | null;
  instagramFollowers?: number | null;
  tiktokHandle?: string | null;
  tiktokFollowers?: number | null;
  spotifyMonthlyListeners?: number | null;
  youtubeSubscribers?: number | null;
}

/** Returns null when the artist has not created a profile yet — a normal state. */
export const getMyProfile = () => api.get<ArtistProfile | null>('/artist/profile');
export const saveMyProfile = (data: ProfileInput) =>
  api.put<ArtistProfile>('/artist/profile', data);

// ── Reliability score ───────────────────────────────────────────────────────

export type ScoreComponent =
  | 'showUpRate' | 'onTimeRate' | 'sellThroughAccuracy'
  | 'promotionalScore' | 'organizerRating' | 'contractComplianceRate';

export interface ArtistScore {
  overallScore: number;
  scoreVisible: boolean;
  buildingTrackRecord: boolean;
  totalEventsBooked: number;
  totalEventsPerformed: number;
  breakdown: Record<ScoreComponent, number>;
  /** Served by the engine from the same constant the calculation uses. */
  weights: {
    showUp: number; onTime: number; sell: number;
    promo: number; org: number; compliance: number;
  };
  visibilityThreshold: number;
  lastCalculatedAt: string | null;
}

export const getMyScore = () => api.get<ArtistScore>('/artist/score');

/**
 * How each component is expressed. The six are NOT all the same kind of number,
 * and rendering them identically would misstate them: four are rates in 0..1,
 * one is a 1–5 promoter score, one is a 1–5 blended rating.
 */
export const SCORE_COMPONENTS: {
  key: ScoreComponent;
  weightKey: keyof ArtistScore['weights'];
  label: string;
  kind: 'rate' | 'stars';
  detail: string;
}[] = [
  {
    key: 'showUpRate', weightKey: 'showUp', label: 'Show-up rate', kind: 'rate',
    detail: 'Events performed as a share of events booked.',
  },
  {
    key: 'onTimeRate', weightKey: 'onTime', label: 'On-time rate', kind: 'rate',
    detail: 'On-time arrivals as a share of events performed.',
  },
  {
    key: 'organizerRating', weightKey: 'org', label: 'Organizer & audience rating', kind: 'stars',
    detail: 'Organizer ratings and verified-attendee reviews, averaged separately then blended 60/40 so neither source’s volume swamps the other.',
  },
  {
    key: 'sellThroughAccuracy', weightKey: 'sell', label: 'Draw accuracy', kind: 'rate',
    detail: 'How closely actual attendance matched what was projected.',
  },
  {
    key: 'contractComplianceRate', weightKey: 'compliance', label: 'Contract compliance', kind: 'rate',
    detail: 'Agreements carried through to performed, against all signed or later.',
  },
  {
    key: 'promotionalScore', weightKey: 'promo', label: 'Promotional effort', kind: 'stars',
    detail: 'Organizer’s 1–5 rating of how hard you promoted the date.',
  },
];

// ── One booking, in full ────────────────────────────────────────────────────

export interface BookingDetail {
  agreementId: string;
  contractNumber: string;
  status: AgreementStatus;
  performanceFeeCents: number;
  depositCents: number;
  balanceCents: number;
  cancellationPenaltyCents: number | null;
  performanceStartTime: string | null;
  setDurationMinutes: number | null;
  contractTerms: string | null;
  organizerSignedAt: string | null;
  artistSignedAt: string | null;
  depositReleased: boolean;
  balanceReleased: boolean;
  contractAvailable: boolean;
  createdAt: string;
  event: {
    eventId: string; title: string; date: string | null;
    time: string | null; venue: string | null; status: string;
  };
  organizer: { name: string | null; email: string };
  /**
   * Set when the request was routed to the artist's agency at creation time.
   * Authority is CONCURRENT, not exclusive: the artist can still answer, and
   * whoever answers first settles it. Reported so the artist knows their agency
   * may also be working on it.
   */
  routedAgency: { agencyId: string; agencyName: string } | null;
  canRespond: boolean;
  canSign: boolean;
}

export const getBooking = (id: string) => api.get<BookingDetail>(`/artist/bookings/${id}`);

/** action='accept' leaves it pending; only 'decline' changes the status (to cancelled). */
export const respondToBooking = (id: string, action: 'accept' | 'decline') =>
  api.put<{ status: AgreementStatus; accepted?: boolean }>(`/bookings/${id}/respond`, { action });

/** The signature is a PNG data URL from SignaturePad. */
export const signBooking = (id: string, signature: string) =>
  api.post<{
    status: AgreementStatus; organizerSigned: boolean;
    artistSigned: boolean; pdfGenerated: boolean;
  }>(`/bookings/${id}/sign`, { signature });

export const contractUrl = (id: string) => `/api/bookings/${id}/contract`;

// ── Ratings and disputes ────────────────────────────────────────────────────

export interface ArtistRating {
  ratingId: string;
  event: { title: string; date: string | null };
  showedUp: boolean | null;
  onTime: boolean | null;
  sellThroughAccuracy: number | null;
  promotionalEffortScore: number | null;
  overallRating: number | null;
  /** The organizer's stated basis for the rating. The organizer is not named. */
  notes: string | null;
  createdAt: string;
  disputed: boolean;
  disputeReason: string | null;
  disputeResolved: boolean;
  disputeResolutionNotes: string | null;
  excludedFromScore: boolean;
  disputeDeadline: string;
  /** Mirrors what fileDispute enforces, so no button is offered that the engine refuses. */
  canDispute: boolean;
  windowClosed: boolean;
}

export const listMyRatings = () => api.get<ArtistRating[]>('/artist/ratings');

export const disputeRating = (ratingId: string, reason: string, evidenceUrl?: string) =>
  api.post<{ disputed: true }>(`/artist/dispute/${ratingId}`, {
    reason, evidenceUrl: evidenceUrl || null,
  });
